"use client"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import type { PageSize, Orientation } from "./types"

interface WorkspaceSettingsTabProps {
  outputFileName: string
  onOutputFileNameChange: (name: string) => void
  pageSize: PageSize
  onPageSizeChange: (size: PageSize) => void
  orientation: Orientation
  onOrientationChange: (orient: Orientation) => void
  margins: number
  onMarginsChange: (value: number) => void
  pageCount: number
  totalImages: number
  pageWidth: number
  pageHeight: number
  disabled: boolean
}

export function WorkspaceSettingsTab({
  outputFileName,
  onOutputFileNameChange,
  pageSize,
  onPageSizeChange,
  orientation,
  onOrientationChange,
  margins,
  onMarginsChange,
  pageCount,
  totalImages,
  pageWidth,
  pageHeight,
  disabled,
}: WorkspaceSettingsTabProps) {
  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label className="text-xs font-medium">Output Name</Label>
        <Input
          value={outputFileName}
          onChange={(e) => onOutputFileNameChange(e.target.value)}
          placeholder="document"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-medium">Page Size</Label>
        <Select
          value={pageSize}
          onValueChange={(v) => onPageSizeChange(v as PageSize)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
            <SelectItem value="A3">A3 (297 × 420 mm)</SelectItem>
            <SelectItem value="A5">A5 (148 × 210 mm)</SelectItem>
            <SelectItem value="Letter">Letter (8.5 × 11 in)</SelectItem>
            <SelectItem value="Legal">Legal (8.5 × 14 in)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-medium">Orientation</Label>
        <Select
          value={orientation}
          onValueChange={(v) => onOrientationChange(v as Orientation)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="portrait">Portrait</SelectItem>
            <SelectItem value="landscape">Landscape</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-medium">
          Margins: {margins}pt ({Math.round(margins / 2.83)}mm)
        </Label>
        <Slider
          value={[margins]}
          min={0}
          max={72}
          step={4}
          onValueChange={([v]) => onMarginsChange(v)}
          disabled={disabled}
        />
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="text-xs font-medium">Document Info</p>
        <div className="text-xs text-muted-foreground space-y-1">
          <div className="flex justify-between">
            <span>Pages:</span>
            <Badge variant="secondary">{pageCount}</Badge>
          </div>
          <div className="flex justify-between">
            <span>Total images:</span>
            <Badge variant="secondary">{totalImages}</Badge>
          </div>
          <div className="flex justify-between">
            <span>Page size:</span>
            <span>
              {pageWidth} × {pageHeight} pt
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
